import * as THREE from 'three'
import type { SharedUniforms } from '../AudioUniforms'
import { BasePrimitive } from './Primitive'
import { hsv2rgb, snoise } from '../shaderChunks'

/**
 * Smoke: a fluid, billowing smoke ring, raymarched volumetrically. The trick for
 * a real-smoke feel (vs a textured donut) is heavy DOMAIN WARPING — the sampling
 * point is advected by noise in two passes, so a very soft torus dissolves into
 * curling volutes that churn. The churn evolves on the energy clock and ramps on
 * the beat; the bass widens the ring, the level/beat brighten it. White, soft.
 */
export class Smoke extends BasePrimitive {
  object3D: THREE.Mesh
  private material: THREE.ShaderMaterial

  constructor(uniforms: SharedUniforms) {
    super(uniforms)
    const half = new THREE.Vector3(9.0, 9.0, 5.5)
    this.material = new THREE.ShaderMaterial({
      uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
      transparent: true,
      depthWrite: false,
      side: THREE.BackSide,
      blending: THREE.NormalBlending,
      vertexShader: /* glsl */ `
        varying vec3 vWorld;
        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorld = wp.xyz;
          gl_Position = projectionMatrix * viewMatrix * wp;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform float uTime, uEnergyTime, uBass, uLevel, uBeat, uBeatPulse, uReactivity, uBassPunch, uHue;
        uniform vec3 uBoxHalf;
        varying vec3 vWorld;
        ${snoise}
        ${hsv2rgb}
        // Ridged fbm -> fibrous smoke strands.
        float ridged(vec3 p) {
          float s = 0.0, a = 0.5;
          for (int i = 0; i < 4; i++) {
            float n = 1.0 - abs(snoise(p));
            s += a * n * n;
            p *= 2.07;
            a *= 0.5;
          }
          return s;
        }
        vec3 warp(vec3 p) { return vec3(snoise(p), snoise(p + 17.1), snoise(p + 43.7)); }
        float density(vec3 p) {
          float t = uEnergyTime * 0.45;
          float R = 3.0 + uBass * uBassPunch * 0.7 * uReactivity;   // ring widens with bass
          float rad = length(p.xy);
          float ang = atan(p.y, p.x);                               // toroidal angle
          vec2 tv = vec2(rad - R, p.z);                             // tube cross-section
          // Moderate warp keeps the ring DEFINED but fluid (more on the beat).
          vec3 w = warp(p * 0.5 + t);
          float churn = 0.45 + uBeatPulse * 0.7 * uReactivity;
          float tubeW = length(tv + w.xy * churn);
          // Strands that run ALONG the ring: toroidal angle as the flow axis so the
          // fibres elongate around the circle (like real smoke-ring wisps).
          float pol = atan(tv.y, tv.x);
          vec3 strandDom = vec3(ang * R * 0.7 + t * 1.3, pol * 1.2, length(tv) * 1.4);
          float strands = ridged(strandDom);
          float shell = smoothstep(1.25, 0.0, tubeW);
          float d = shell * clamp(strands * 0.85, 0.0, 1.0);
          d *= 0.8 + uLevel * 0.6 + uBeatPulse * 0.4 * uReactivity;
          return clamp(d, 0.0, 1.0);
        }
        void main() {
          vec3 ro = cameraPosition;
          vec3 rd = normalize(vWorld - ro);
          vec3 t1 = (-uBoxHalf - ro) / rd;
          vec3 t2 = (uBoxHalf - ro) / rd;
          vec3 tmin = min(t1, t2), tmax = max(t1, t2);
          float tn = max(max(tmin.x, tmin.y), tmin.z);
          float tf = min(min(tmax.x, tmax.y), tmax.z);
          tn = max(tn, 0.0);
          if (tn >= tf) discard;
          const int STEPS = 46;
          float dt = (tf - tn) / float(STEPS);
          float t = tn + dt * 0.5;
          float trans = 1.0;
          float lum = 0.0;
          for (int i = 0; i < STEPS; i++) {
            vec3 p = ro + rd * t;
            float dens = density(p) * dt * 1.7;
            if (dens > 0.001) {
              lum += trans * dens;
              trans *= 1.0 - dens;
              if (trans < 0.02) break;
            }
            t += dt;
          }
          float alpha = 1.0 - trans;
          if (alpha < 0.01) discard;
          vec3 col = hsv2rgb(vec3(uHue, 0.07, 0.78 + uBeatPulse * 0.22 * uReactivity)) * (0.6 + lum * 0.7);
          gl_FragColor = vec4(col, alpha);
        }
      `,
    })
    this.material.uniforms.uBoxHalf = { value: half }
    this.object3D = new THREE.Mesh(new THREE.BoxGeometry(half.x * 2, half.y * 2, half.z * 2), this.material)
    this.object3D.frustumCulled = false
  }

  rebuild(): void {}
}
